import * as bootstrap from "bootstrap";

const COOKIE_TTL_DAYS = 3;
const PERSISTENT_ALERT_SELECTOR = "[data-alert-persist-key]";
const AUTO_DISMISS_ALERT_SELECTOR = "[data-alert-auto-dismiss]";
const ALERT_SELECTOR = `${PERSISTENT_ALERT_SELECTOR}, ${AUTO_DISMISS_ALERT_SELECTOR}`;

function isPersistentAlert(alert) {
  return alert instanceof Element && alert.matches(PERSISTENT_ALERT_SELECTOR);
}

function getAutoDismissDelay(alert) {
  const rawValue = Number.parseInt(String(alert?.dataset?.alertAutoDismiss ?? "").trim(), 10);

  if (!Number.isFinite(rawValue) || rawValue <= 0) {
    return null;
  }

  return rawValue;
}

function initAutoDismiss(alert) {
  const delay = getAutoDismissDelay(alert);
  if (delay === null) {
    return;
  }

  window.setTimeout(() => {
    if (!alert.isConnected) {
      return;
    }

    const alertInstance = bootstrap.Alert.getOrCreateInstance(alert);
    alertInstance.close();
  }, delay);
}

function getAlertPersistKey(alert) {
  const explicitKey = String(alert?.dataset?.alertPersistKey ?? "").trim();
  if (explicitKey !== "") {
    return explicitKey;
  }

  const fallbackText = String(alert?.textContent ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, 120);

  return `${window.location.pathname}:${fallbackText}`;
}

function normalizeCookieName(value) {
  return `sheet_alert_${String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "")}`;
}

function getCookie(name) {
  const encodedName = `${encodeURIComponent(name)}=`;
  const cookies = document.cookie ? document.cookie.split(";") : [];

  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(encodedName)) {
      return decodeURIComponent(trimmed.slice(encodedName.length));
    }
  }

  return "";
}

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function getAlertVariant(alert) {
  if (!(alert instanceof Element)) {
    return "secondary";
  }

  if (alert.classList.contains("alert-warning")) {
    return "warning";
  }

  if (alert.classList.contains("alert-danger")) {
    return "danger";
  }

  if (alert.classList.contains("alert-success")) {
    return "success";
  }

  if (alert.classList.contains("alert-info")) {
    return "info";
  }

  return "secondary";
}

function getAlertShortcutLabel(alert) {
  return String(alert.getAttribute("data-alert-shortcut-label") ?? "Pokaż komunikat").trim() || "Pokaż komunikat";
}

function restorePersistentAlert(alert, cookieName, shortcut) {
  setCookie(cookieName, "", -1);
  alert.hidden = false;
  shortcut.remove();
  initAlert(alert);
  alert.scrollIntoView({ behavior: "smooth", block: "nearest" });
}

function ensureShortcutButton(alert) {
  const card = alert.closest(".card--sheet");
  const shortcutHost = card?.querySelector("[data-sheet-alert-shortcuts]");

  if (!(shortcutHost instanceof HTMLElement)) {
    return false;
  }

  const cookieName = normalizeCookieName(getAlertPersistKey(alert));
  let shortcut = shortcutHost.querySelector(`[data-alert-shortcut-for="${cookieName}"]`);

  if (!(shortcut instanceof HTMLButtonElement)) {
    shortcut = document.createElement("button");
    shortcut.type = "button";
    shortcut.className = `sheet-alert-shortcut sheet-alert-shortcut--${getAlertVariant(alert)}`;
    shortcut.dataset.alertShortcutFor = cookieName;
    shortcut.innerHTML = '<i class="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>';
    shortcut.addEventListener("click", () => restorePersistentAlert(alert, cookieName, shortcut));
    shortcutHost.appendChild(shortcut);
  }

  const shortcutLabel = getAlertShortcutLabel(alert);
  shortcut.setAttribute("aria-label", shortcutLabel);
  shortcut.setAttribute("title", shortcutLabel);

  return true;
}

function initAlert(alert) {
  if (!(alert instanceof HTMLElement)) {
    return;
  }

  if (!isPersistentAlert(alert)) {
    if (alert.dataset.alertPersistInitialized !== "1") {
      alert.dataset.alertPersistInitialized = "1";
      initAutoDismiss(alert);
    }
    return;
  }

  const cookieName = normalizeCookieName(getAlertPersistKey(alert));
  if (getCookie(cookieName) === "1") {
    ensureShortcutButton(alert);
    alert.hidden = true;
    return;
  }

  // Persistent alerts are rendered hidden so the server response cannot flash an
  // alert that was dismissed in an earlier visit. Reveal it only after its state
  // has been checked in the browser.
  alert.hidden = false;

  if (alert.dataset.alertPersistInitialized === "1") {
    return;
  }

  alert.dataset.alertPersistInitialized = "1";

  // Cancel Bootstrap's removal even when dismissal runs in its delegated capture listener.
  alert.addEventListener("close.bs.alert", (event) => {
    event.preventDefault();
    setCookie(cookieName, "1", COOKIE_TTL_DAYS);
    alert.hidden = true;
    ensureShortcutButton(alert);
  });
}

function collectAlerts(root = document) {
  const alerts = [];

  if (root instanceof Element && root.matches(ALERT_SELECTOR)) {
    alerts.push(root);
  }

  if (root instanceof Element || root instanceof Document) {
    alerts.push(...root.querySelectorAll(ALERT_SELECTOR));
  }

  return alerts;
}

export function initDismissibleAlerts(root = document) {
  collectAlerts(root).forEach((alert) => initAlert(alert));
}
